<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';
import Button from 'next/button/Button.vue';

const emit = defineEmits(['start-phase', 'open-home']);

const store = useStore();
const { t, tm, rt } = useI18n();

const PHASES = [
  { key: 'getting_started', icon: 'i-lucide-building-2' },
  { key: 'starting_with_ai', icon: 'i-lucide-bot' },
  { key: 'pricing', icon: 'i-lucide-badge-euro', isCall: true },
  {
    key: 'growth',
    icon: 'i-lucide-sparkles',
    isCall: true,
    tracks: ['automate', 'quotes'],
  },
];

const hub = computed(() => store.state.onboarding.hub);
const completedCount = computed(() => hub.value?.completed || 0);
const totalCount = computed(() => hub.value?.total || 4);
const progressPct = computed(() =>
  `${Math.round((completedCount.value / totalCount.value) * 100)}%`
);
const allDone = computed(() => completedCount.value >= totalCount.value);

function phaseStatus(key) {
  if (hub.value?.phases?.[key]?.completed_at) return 'done';
  if (key === 'getting_started') {
    const step = store.state.onboarding.step;
    const domain = store.state.onboarding.domain;
    if (domain || step > 0) return 'in_progress';
  }
  if (key === 'starting_with_ai') {
    const step = store.state.onboarding.step;
    if ([4, 5, 6, 7].includes(step) && store.state.onboarding.assistantName) {
      return 'in_progress';
    }
  }
  return 'todo';
}

function isPhaseLocked(index) {
  if (index === 0) return false;
  const previous = PHASES[index - 1];
  return phaseStatus(previous.key) !== 'done';
}

function ctaLabel(key, status) {
  if (status === 'in_progress') {
    return t(`ONBOARDING_WIZARD.OVERVIEW.PHASES.${key}.CTA`);
  }
  return t(`ONBOARDING_WIZARD.OVERVIEW.PHASES.${key}.CTA_START`);
}

function startPhase(key, track = null) {
  const index = PHASES.findIndex(phase => phase.key === key);
  if (index < 0 || isPhaseLocked(index) || phaseStatus(key) === 'done') return;
  emit('start-phase', { phase: key, track });
}

function bookSetupCall() {
  const callPhase = PHASES.find(
    (phase, index) =>
      phase.isCall &&
      !isPhaseLocked(index) &&
      phaseStatus(phase.key) !== 'done'
  );
  if (callPhase) {
    startPhase(callPhase.key, callPhase.tracks?.[0] || null);
    return;
  }
  // Fallback: open first unlocked call phase, or pricing if all done.
  startPhase('pricing');
}
</script>

<template>
  <section class="w-full max-w-5xl mx-auto flex flex-col gap-10">
    <header class="flex flex-col gap-6 w-full">
      <div class="flex flex-col gap-3">
        <h1
          class="m-0 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 dark:text-white leading-tight"
        >
          {{ t('ONBOARDING_WIZARD.OVERVIEW.TITLE') }}
        </h1>
        <p class="m-0 text-base leading-6 text-slate-500 dark:text-slate-400">
          {{ t('ONBOARDING_WIZARD.OVERVIEW.DESCRIPTION') }}
        </p>
      </div>

      <div class="flex items-center gap-4">
        <div
          class="flex-1 h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden"
          role="progressbar"
          :aria-valuemin="0"
          :aria-valuemax="totalCount"
          :aria-valuenow="completedCount"
          :aria-label="
            t('ONBOARDING_WIZARD.PROGRESS', {
              completed: completedCount,
              total: totalCount,
            })
          "
        >
          <div
            class="h-full rounded-full bg-woot-500 transition-all duration-300"
            :style="{ width: progressPct }"
          />
        </div>
        <span
          class="text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap tabular-nums"
        >
          {{
            t('ONBOARDING_WIZARD.PROGRESS', {
              completed: completedCount,
              total: totalCount,
            })
          }}
        </span>
      </div>
    </header>

    <div
      v-if="allDone"
      class="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-4 rounded-2xl bg-green-50 dark:bg-green-900/20 text-green-900 dark:text-green-100 text-sm leading-5"
    >
      <div class="flex items-start gap-3 flex-1 min-w-0">
        <Icon icon="i-lucide-check" class="size-4 shrink-0 mt-0.5" />
        <span>{{ t('ONBOARDING_WIZARD.OVERVIEW.ALL_DONE') }}</span>
      </div>
      <Button
        sm
        class="shrink-0 self-start sm:self-center"
        :label="t('ONBOARDING_WIZARD.OVERVIEW.OPEN_DASHBOARD')"
        @click="emit('open-home')"
      />
    </div>

    <ol
      class="m-0 p-0 list-none grid grid-cols-1 md:grid-cols-2 gap-5"
      :aria-label="t('ONBOARDING_WIZARD.HEADER')"
    >
      <li
        v-for="(phase, index) in PHASES"
        :key="phase.key"
        class="relative flex flex-col min-h-[220px] rounded-2xl border p-6 transition-shadow"
        :class="{
          'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/40 hover:shadow-md':
            !isPhaseLocked(index) && phaseStatus(phase.key) !== 'done',
          'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/40 opacity-80':
            phaseStatus(phase.key) === 'done',
          'border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/20 opacity-55 pointer-events-none':
            isPhaseLocked(index),
        }"
        :aria-disabled="isPhaseLocked(index) ? 'true' : undefined"
      >
        <div class="mb-5">
          <span
            class="size-10 rounded-full flex items-center justify-center"
            :class="{
              'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300':
                phaseStatus(phase.key) === 'done',
              'bg-woot-50 text-woot-600 dark:bg-woot-900/40 dark:text-woot-300':
                !isPhaseLocked(index) && phaseStatus(phase.key) !== 'done',
              'bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500':
                isPhaseLocked(index),
            }"
          >
            <Icon
              :icon="
                phaseStatus(phase.key) === 'done' ? 'i-lucide-check' : phase.icon
              "
              class="size-5"
            />
          </span>
        </div>

        <h2
          class="m-0 mb-2 text-lg font-semibold leading-snug"
          :class="
            isPhaseLocked(index)
              ? 'text-slate-400 dark:text-slate-500'
              : 'text-slate-900 dark:text-white'
          "
        >
          {{ t(`ONBOARDING_WIZARD.OVERVIEW.PHASES.${phase.key}.TITLE`) }}
        </h2>

        <p
          class="m-0 mb-3 text-sm leading-6"
          :class="
            isPhaseLocked(index)
              ? 'text-slate-400 dark:text-slate-600'
              : 'text-slate-500 dark:text-slate-400'
          "
        >
          {{
            t(`ONBOARDING_WIZARD.OVERVIEW.PHASES.${phase.key}.DESCRIPTION`)
          }}
        </p>

        <ul
          class="m-0 p-0 list-none flex flex-col gap-2 flex-1"
          :class="
            isPhaseLocked(index)
              ? 'text-slate-400 dark:text-slate-600'
              : 'text-slate-600 dark:text-slate-300'
          "
        >
          <li
            v-for="(value, valueIndex) in tm(
              `ONBOARDING_WIZARD.OVERVIEW.PHASES.${phase.key}.VALUES`
            )"
            :key="valueIndex"
            class="flex items-start gap-2 text-sm leading-5"
          >
            <Icon
              icon="i-lucide-check"
              class="size-4 shrink-0 mt-0.5"
              :class="
                isPhaseLocked(index)
                  ? 'text-slate-300 dark:text-slate-600'
                  : 'text-woot-500'
              "
            />
            <span>{{ rt(value) }}</span>
          </li>
        </ul>

        <div
          v-if="
            phase.tracks &&
            !isPhaseLocked(index) &&
            phaseStatus(phase.key) !== 'done'
          "
          class="flex flex-wrap gap-x-3 gap-y-1 mt-4"
        >
          <button
            v-for="track in phase.tracks"
            :key="track"
            type="button"
            class="text-sm text-woot-600 dark:text-woot-400 hover:underline underline-offset-2"
            @click="startPhase('growth', track)"
          >
            {{ t(`ONBOARDING_WIZARD.GROWTH.${track.toUpperCase()}.TITLE`) }}
          </button>
        </div>

        <div
          v-if="!isPhaseLocked(index) && phaseStatus(phase.key) !== 'done'"
          class="mt-6 pt-1"
        >
          <Button
            v-if="!phase.tracks"
            sm
            outline
            trailing-icon
            icon="i-lucide-arrow-right"
            :label="ctaLabel(phase.key, phaseStatus(phase.key))"
            @click="startPhase(phase.key)"
          />
          <Button
            v-else
            sm
            outline
            trailing-icon
            icon="i-lucide-arrow-right"
            :label="t('ONBOARDING_WIZARD.OVERVIEW.PHASES.pricing.CTA_START')"
            @click="startPhase('growth', 'automate')"
          />
        </div>
      </li>
    </ol>

    <p class="m-0 text-sm text-slate-500 dark:text-slate-400">
      {{ t('ONBOARDING_WIZARD.OVERVIEW.HELP_PREFIX') }}
      <button
        type="button"
        class="text-woot-600 dark:text-woot-400 hover:underline underline-offset-2 font-medium"
        @click="bookSetupCall"
      >
        {{ t('ONBOARDING_WIZARD.OVERVIEW.HELP_CALL') }}
      </button>
      {{ t('ONBOARDING_WIZARD.OVERVIEW.HELP_SUFFIX') }}
    </p>
  </section>
</template>
